/**
 * SidebarDatePicker
 * A compact date picker for the task sidebar header.
 * Shows "Today" when viewing today, or the formatted date (e.g., "Sat, Feb 18")
 * with a popover calendar for date navigation.
 * When a non-today date is selected, a "T" button appears to jump back to today.
 */

import { useState } from 'react';
import { format, isSameDay } from 'date-fns';
import { ChevronDown } from 'lucide-react';
import { getCampaignToday } from '@/lib/calendarUtils';
import { useCalendarStore } from '@/stores/calendarStore';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';

export function SidebarDatePicker() {
  const [open, setOpen] = useState(false);
  const selectedDate = useCalendarStore((state) => state.selectedDate);
  const setSelectedDate = useCalendarStore((state) => state.setSelectedDate);
  const goToToday = useCalendarStore((state) => state.goToToday);

  const today = getCampaignToday();
  const isToday = isSameDay(selectedDate, today);

  const label = isToday ? 'Today' : format(selectedDate, 'EEE, MMM d');

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      setOpen(false);
    }
  };

  const handleGoToToday = () => {
    goToToday();
    setOpen(false);
  };

  return (
    <div className="flex items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'h-7 gap-1 px-2 text-xs font-medium',
              'border border-border/50 rounded-full',
              'hover:bg-accent'
            )}
          >
            {label}
            <ChevronDown className="h-3 w-3 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" sideOffset={6}>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            defaultMonth={selectedDate}
            today={today}
          />
        </PopoverContent>
      </Popover>

      {!isToday && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-xs font-bold text-primary hover:bg-accent"
          onClick={handleGoToToday}
          title="Go to today"
        >
          T
        </Button>
      )}
    </div>
  );
}
